/**
 * Role policy for the hospital feature.
 *
 * The backend sends the signed-in user's role two ways:
 *
 *   active_role      "مدقق فني"              Arabic display label
 *   active_role_key  "is_technical_auditor"  stable, never translated
 *
 * `active_role_key` is authoritative. The labels are `gettext_lazy` strings,
 * so gating on them breaks silently the moment a translation is edited or a
 * locale is added. The label match is kept only as a fallback for deployments
 * that predate the key, and should be deleted once every environment sends it.
 *
 * `active_role_key` is explicitly `null` when the user has no active role, or
 * when the stored value matches no known role. That must DENY, not fall
 * through to the label — which is why the two cases are distinguished below.
 */
const matchesRole = (roleKey: string | null | undefined, keys: readonly string[]): boolean =>
  !!roleKey && keys.includes(roleKey);

/* ------------------------------------------------------------------ *
 * Hospital column on the Cases grid
 * ------------------------------------------------------------------ */

/**
 * "Auditor" in the acceptance criteria means the FINANCIAL auditors, not the
 * content auditors. Technical, Medical and Marketing Audit must NOT see the
 * column, nor must the Operations Manager or System Administrator.
 *
 * Mirrors the backend's export column policy so the on-screen grid and the
 * exported file agree. That policy explicitly REMOVES the hospital column for
 * Admin, Technical/Medical/Marketing Audit and Operations Manager, and
 * explicitly KEEPS it for the four financial-chain roles below.
 *
 * The four share one identical column set by design - they act on the same
 * case in sequence, so a reviewer seeing different columns from the auditor
 * either side of them would be a bug. The backend has a test pinning that.
 */
const HOSPITAL_COLUMN_KEYS: readonly string[] = [
  "is_first_audit",
  "is_second_audit",
  "is_financial_reviewer",
  "is_transfer_manager",
];


/**
 * Charity users, kept visible by DECISION rather than by default.
 *
 * They have no entry in the backend's export policy, and in that map "no
 * entry" means "remove nothing", not "show nothing". For charity users that
 * is deliberate: their grid is scoped to their own cases, so the hospital,
 * invoice number and amount columns are their OWN invoice data - hiding them
 * would hide a charity's invoice from the charity that uploaded it.
 *
 * Nothing leaks either: a charity can never see a hospital-channel case at
 * all, since those belong to the Shefa Charity, so the column is simply blank
 * for them.
 */
const HOSPITAL_COLUMN_CHARITY_KEYS: readonly string[] = [
  "is_charity_manager",
  "is_charity_representative",
];


/**
 * Gates the COLUMN only. Access to the Cases list itself is enforced by the
 * backend; there is deliberately no second check for that here.
 *
 * KNOWN DIVERGENCE: MOH Representative and Endowment Admin also have no entry
 * in the export policy, so the exported file shows them every column while
 * this grid does not. Neither is in the hospital flow and neither has been
 * ruled on, so they are left out rather than silently granted.
 *
 * Low impact in practice: backend-confirmed that neither role has the cases
 * list in its menu at all (MOH sees only treatment requests, Endowment Admin
 * only the waqf product area), so neither is routed here by the nav. Note
 * though that the endpoint itself IS reachable by URL for both - the grid's
 * gate passes on user_type == ADMIN - so this is a statement about navigation,
 * not about access.
 *
 * Pending a ruling. The backend owner recommends giving both explicit entries
 * that REMOVE the column, under which this list is already correct; mirroring
 * the export instead is a one-line addition of their two keys here.
 */
export const canSeeHospitalColumn = (roleKey?: string | null): boolean =>
  matchesRole(roleKey, [...HOSPITAL_COLUMN_KEYS, ...HOSPITAL_COLUMN_CHARITY_KEYS]);

/* ------------------------------------------------------------------ *
 * Creating a hospital treatment request
 * ------------------------------------------------------------------ */

const HOSPITAL_REQUEST_CREATOR_KEYS: readonly string[] = [
  "is_technical_auditor",
  "is_operations_manager",
];


/**
 * Gates the "create hospital treatment request" button. The NAV item is
 * backend-driven and gated there; this is only the button.
 */
export const canCreateHospitalRequest = (roleKey?: string | null): boolean =>
  matchesRole(roleKey, HOSPITAL_REQUEST_CREATOR_KEYS);

/* ------------------------------------------------------------------ *
 * Technical Auditor: read-only on an EXISTING hospital request (SHEFA2-4602)
 * ------------------------------------------------------------------ */

const TECHNICAL_AUDITOR_KEYS: readonly string[] = ["is_technical_auditor"];

/**
 * Gates the hospital edit route only - creating one is unaffected (see
 * `canCreateHospitalRequest` above, which still includes this role). The
 * Technical Auditor may view and may raise a new hospital treatment request,
 * but may never edit an existing one, regardless of its status - unlike the
 * other admin-type roles, whose edit access additionally depends on status
 * (see Details.vue's own `authorizedRoles` check).
 */
export const isTechnicalAuditorRole = (roleKey?: string | null): boolean =>
  matchesRole(roleKey, TECHNICAL_AUDITOR_KEYS);
